import React, { ReactElement } from 'react';
import {
  Component,
  FilterCombo,
  noDataDropDownRender,
  PuzzleTagField,
  sortByProperty,
  VBox,
  withFormControl,
  Field
} from "puzzle"
import { preventDisabledTagToOpen, targetLanguageItemColoring, TM_DATA, tmTagRenderer } from "../../../../util/Util";
import _ from "lodash";
import { SOURCE_LANGUAGE } from "../../../Types";
import { config } from "./PopupLanguages.config";
import { LANGUAGE } from "../../Details/Details";
import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { FieldWrap } from "../../../../UI/FieldWrap/FieldWrap";


export class PopupLanguages extends Component<{}, any> {

  state = {
    targetLanguageData: []
  };

  /**
   * Sets Submission Details data to DetailsPopup
   */
  setData = () => {
    const { sourceLanguage, targetLanguage } = this.props.detailsProps.form.getData();
    const { languageDirectionData } = this.props.detailsState;

    if (!!sourceLanguage) {
      this.props.form.setData({
        sourceLanguage,
        targetLanguage
      });

      let selectedItem: any = languageDirectionData.find((item: any) => {
        return item.source === sourceLanguage;
      });
      if (selectedItem) {
        const { target } = selectedItem;
        this.setState({
          targetLanguageData: sortByProperty(target, 'targetLanguageDisplayName')
        });
      }
    }
  };

  /**
   * Handle source language change. Find and set data for target language
   */
  handleSourceLanguageChange = (value: SOURCE_LANGUAGE | string) => {
    if (value) {
      let { languageDirectionData } = this.props.detailsState;
      let selectedItem: any = languageDirectionData.find((item: any) => {
        return item.source === value;
      });

      if (selectedItem) {
        const { target } = selectedItem;
        const defaultLanguages = this.getDefaultTargetLanguages(target);
        this.props.form.setData({ targetLanguage: defaultLanguages });
        this.setState({
          targetLanguageData: sortByProperty(target, 'targetLanguageDisplayName')
        });
      }
    } else {
      this.props.form.setData({ targetLanguage: [] });
    }
  };

  /**
   * Returns currently stored data whit in the form
   */
  getLanguagesData = () => {
    const { sourceLanguage, targetLanguage } = this.props.form.getData();
    return {
      targetLanguage,
      sourceLanguage
    }
  };

  /**
   * Compares language data received through props (details.form) and languages data with in components form.
   */
  hasDataChange = () => {
    const { sourceLanguage, targetLanguage } = this.props.detailsProps.form.getData();
    const { sourceLanguage: currentSourceLanguage, targetLanguage: currentTargetLanguage } = this.props.form.getData();

    return !(_.isEqual(targetLanguage, currentTargetLanguage) && _.isEqual(sourceLanguage, currentSourceLanguage))
  };

  /**
   * Handle target language change
   */
  handleTargetLanguageChange = (event: ComboBoxChangeEvent) => {
    this.props.form.handleChange(event);
  };

  /**
   * Returns default target language data for source lang
   * @param languageDirectionData
   */
  getDefaultTargetLanguages = (languageDirectionData: LANGUAGE[]) => {
    return languageDirectionData.filter((item: LANGUAGE) => item.default).map((item: LANGUAGE) => item.targetLanguage);
  };

  /**
   * Source language change handler
   * @param event
   */
  onSourceLanguageChange = (event: ComboBoxChangeEvent) => {
    let value = event.target.value;
    this.handleSourceLanguageChange(value);
    this.props.form.handleChange(event);
  };

  /**
   * Target language field should be disabled when source language is invalid/empty
   */
  isTargetLanguageDisabled = () => {
    const { sourceLanguage } = this.props.form.getFields();
    return sourceLanguage.valid === false;
  };

  /**
   * Returns full target language object for given language directions
   */
  getSelectedTargetLanguages = () => {
    let selectedTargetLanguages = this.state.targetLanguageData.filter((item: any) => this.props.form.getData().targetLanguage.includes(item.targetLanguage));
    return selectedTargetLanguages
  };

  renderBody = () => {
    const {
      languageDirectionData,
      startedSubmission,
    } = this.props.detailsState;
    const { targetLanguageData } = this.state;
    const { getFields } = this.props.form;
    const {
      sourceLanguage,
      targetLanguage,
    } = getFields();
    const {
      isMultiBatch,
      project = undefined,
      paClient
    } = this.props.detailsProps.form.getData();

    return (
      <VBox htmlTag={'form'} className={'k-form'} flex1>
        <FieldWrap withValidation>
          <Field mandatory fieldClassName={'k-flex-none'}>
            <FilterCombo
              label={this.translate('sourceLanguage')}
              name="sourceLanguage"
              data={languageDirectionData}
              textField='sourceLanguageDisplayName'
              onChange={this.onSourceLanguageChange}
              valueField='source'
              handleFormChange={this.props.form.handleChange}
              disabled={startedSubmission}
              listNoDataRender={() => noDataDropDownRender(this.translate('emptySourceLanguage'))}
              translateMethod={this.translate.bind(this)}
              valueAsSimpleType={true}
              withValidation
              errorTxt={this.translateErrorMessage(sourceLanguage)}
              {...sourceLanguage}
            />
          </Field>
        </FieldWrap>
        {!isMultiBatch?.value && (<FieldWrap withValidation className='!k-flex-1'>
          <Field mandatory fieldClassName='k-flex-1 tp-height-100 pd-target-lang-field'>
            <PuzzleTagField
              autoClose={false}
              className={'tp-target-lang-tagfield'}
              data={targetLanguageData}
              disabled={this.isTargetLanguageDisabled() || startedSubmission}
              errorTxt={this.translateErrorMessage(targetLanguage)}
              itemColoring={targetLanguageItemColoring.bind(this)}
              label={this.translate('targetLanguage')}
              markInRed={true}
              name="targetLanguage"
              onChange={this.handleTargetLanguageChange}
              onOpen={preventDisabledTagToOpen}
              required={sourceLanguage.value}
              showCount
              tagRender={(data: TM_DATA, li: ReactElement<any>) => tmTagRenderer(data, li, this.dispatch.bind(this), project, paClient)}
              textField='targetLanguageDisplayName'
              valueField='targetLanguage'
              withValidation
              {...targetLanguage}
            />
          </Field>
        </FieldWrap>)}
      </VBox>
    )
  }
}

export default withFormControl(config, false)(PopupLanguages);
